import { Controller } from "@hotwired/stimulus"
import { escapeHtml } from "../escape_html"

// A searchable dropdown over a real <select>, for lists we hold ourselves.
//
// The closed field already looked right — select.field draws its own chevron
// and border — but clicking it opened the OS list: system font, system
// highlight, different on every platform, and nothing like the food picker
// sitting a few rows below it in the same form.
//
// Not the combobox controller, which looks the same and works differently:
// that one fetches from the server on every keystroke because the food
// library is ~8,000 rows, and it knows about brands, estimated values and
// creating a food that does not exist yet. These lists are twenty-odd values
// we define, so the options ship with the page and filtering is local. Sharing
// a controller would have meant branching most of its methods.
//
// The <select> stays the form's source of truth and keeps working with no JS.
// Options come from it too, so growing a list is a Ruby change and nothing
// here needs touching.
export default class extends Controller {
  static targets = ["select", "input", "list", "caret"]

  connect() {
    this.readOptions()
    this.selectTarget.classList.add("sr-only")
    this.selectTarget.tabIndex = -1

    // A LIST TO BROWSE, NOT A FIELD TO TYPE IN — on touch only.
    //
    // The keyboard was the whole problem: it opened on first focus and
    // covered the list it was meant to filter, leaving a strip too thin to
    // scroll. readonly stops it — a readonly input still takes clicks and
    // still fires keydown, and iOS opens no keyboard for one — so the list
    // gets the whole screen.
    //
    // NO WAY BACK TO TYPING ON TOUCH, and three attempts say there is not
    // one. iOS shows the keyboard only for a genuine user gesture on an
    // already-writable field: lifting readonly mid-tap does not qualify
    // (2ed13ce), and neither does a programmatic blur/focus (0a50027).
    // Cody, 11 Sep, on the last of them: "doesnt open keyboard on first
    // focus (good) but then cant tap again to type text."
    //
    // That is an acceptable trade rather than a gap. These are closed
    // lists — 32 category rows at the largest — and browsing a full screen
    // of them is the interaction a <select> would have given anyway.
    // Desktop keeps type-ahead untouched.
    this.touch = window.matchMedia("(hover: none)").matches ||
                 navigator.maxTouchPoints > 0
    // NOT A TEXT INPUT AT ALL ON TOUCH.
    //
    // readonly stops the keyboard but the element is still an
    // <input type="text">, and iOS puts its AutoFill and Writing Tools bar
    // at the top of the screen for any focused one — readonly, inputmode
    // none, aria-readonly and all. Cody, 11 Sep, after each of those:
    // "the autofill/writing tools bar is still popping up at the top of
    // the page."
    //
    // Changing the TYPE is what stops it: a button is not a text field, so
    // there is nothing for the platform to offer to fill in or rewrite.
    // Safe because typing is already blocked here — the input keeps its
    // value, its id and its events, and only stops claiming to be text.
    if (this.touch) {
      this.inputTarget.type = "button"
      this.inputTarget.readOnly = true
    }
    this.inputTarget.hidden = false
    // The caret shows with the input it belongs to — see the partial.
    if (this.hasCaretTarget) this.caretTarget.hidden = false
    this.activeIndex = -1
    this.syncInputFromSelect()

    // A form reset, an autofill, or another controller rewriting the options
    // — the ingredient rows repopulate their unit list whenever the food
    // changes, so the cached options have to be re-read rather than kept
    // from connect().
    this.onExternalChange = () => {
      this.readOptions()
      this.syncInputFromSelect()
    }
    this.selectTarget.addEventListener("change", this.onExternalChange)

    // A click ANYWHERE else closes the list.
    //
    // Blur alone was not enough: clicking a heading, a card, or empty page
    // moves focus nowhere, so no blur fires and the list stayed open behind
    // whatever you clicked. Several open at once was possible.
    //
    // mousedown, not click, and capture: it has to beat the list's own
    // mousedown handler for a click landing outside, while a click INSIDE is
    // let through by the containment check.
    this.onOutsideClick = (event) => {
      if (this.listTarget.hidden) return
      if (this.element.contains(event.target)) return

      this.close()
    }
    document.addEventListener("mousedown", this.onOutsideClick, true)
  }

  disconnect() {
    this.selectTarget.removeEventListener("change", this.onExternalChange)
    document.removeEventListener("mousedown", this.onOutsideClick, true)
    if (this.onViewportChange && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.onViewportChange)
      window.visualViewport.removeEventListener("scroll", this.onViewportChange)
    }
  }

  // The option's GROUP travels with it, when the select has optgroups —
  // twenty-six food categories are a scroll rather than a choice, and the
  // headings are what make the list scannable.
  //
  // The array stays FLAT even so. Keyboard navigation and choose() are both
  // index-based, and a nested structure would mean every one of them
  // learning about groups; render() draws a heading whenever the group
  // changes from the row before, which needs nothing but order.
  readOptions() {
    this.options = Array.from(this.selectTarget.options).map((option) => ({
      value: option.value,
      label: option.text,
      group: option.parentElement?.label || null
    }))
  }

  // LIFTS THE WHOLE ROW, not just this wrapper. An open list is painted
  // inside whatever ancestors it sits in, and in the picker that is a row
  // painted before the rows below it — so a z-index here, or on the wrapper,
  // only ever competes with this row's own children. The list went under the
  // next result either way.
  //
  // Marked on the nearest ancestor that opted in, and only while open, so a
  // resting list disturbs nothing. See .is-lifted.
  lift() {
    this.lifted = this.element.closest(".is-liftable") || this.element
    this.lifted.classList.add("is-lifted")
  }

  drop() {
    if (this.lifted) this.lifted.classList.remove("is-lifted")
  }

  // THE TAP HANDLER FOR THE BUTTON SHAPE.
  //
  // A text input opens the list by focusing; a button never focuses on
  // tap, so it needs the click. Both fire on a desktop — focus first — so
  // this must not close what that focus just opened: a click arriving
  // within a moment of the open belongs to the same gesture.
  toggle() {
    if (Date.now() - (this.openedAt || 0) < 400) return

    if (this.listTarget.hidden) this.open()
    else this.close()
  }

  open() {
    this.openedAt = Date.now()

    // Re-read on open too. foodChanged rewrites the unit list by replacing
    // the option nodes directly, which fires nothing to listen for.
    this.readOptions()

    // Everything, not the current value's matches — opening the list to a
    // single option because a value is already set makes it look broken.
    //
    // Not on touch: select() focuses, and focus is what brings the
    // keyboard up over the list this control is trying to show.
    if (!this.touch) this.inputTarget.select()
    this.render(this.options)
    this.listTarget.hidden = false
    this.placePanel()
    this.watchViewport()
    this.lift()
    this.activeIndex = this.options.findIndex((o) => o.value === this.selectTarget.value)
    this.highlight()
    this.scrollActiveIntoView()
  }

  // ABOVE THE FIELD WHEN THERE IS NO ROOM BELOW IT.
  //
  // The panel is `top-full` in CSS, which is right on a desktop and wrong
  // on a phone: the software keyboard covers the bottom half of the screen
  // the moment this field takes focus, so a list rendered below the field
  // opens underneath the keyboard and looks like nothing happened. Cody,
  // 11 Sep, on the category picker.
  //
  // MEASURED AGAINST visualViewport, NOT window.innerHeight. innerHeight
  // does not change when the keyboard appears — that is the whole reason
  // the bug is invisible on a desktop and on a resized browser window.
  // visualViewport is the only thing that reports the space actually left.
  //
  // Flipped only when there is genuinely more room above: a short field
  // near the top of a small screen has no good answer, and dropping down
  // is the better of the two because the list can at least scroll.
  // WHERE THE LIST GOES, AND HOW TALL IT IS ALLOWED TO BE.
  //
  // On a phone the keyboard takes the bottom half of the screen the moment
  // the field is focused, so a list rendered below the field lands under
  // it — and what is left is a strip too thin to scroll. Cody, 11 Sep:
  // "you have to scroll down outside of the select (which is a thin strip)
  // so you can actually get down to scroll the dropdown."
  //
  // Two halves, and the height is the half that was missing: flipping to
  // the roomier side is no good if the panel is then taller than that side
  // and spills off-screen anyway.
  //
  // MEASURED AGAINST visualViewport. innerHeight does not change when the
  // keyboard appears, which is exactly why this looks fine on a desktop.
  placePanel() {
    const panel = this.listTarget
    panel.classList.remove("bottom-full", "mb-1", "top-full", "mt-1")

    const viewport = window.visualViewport?.height ?? window.innerHeight
    const field = this.inputTarget.getBoundingClientRect()
    const gap = 4
    const below = viewport - field.bottom - gap
    const above = field.top - gap

    // 16rem, the panel's own cap — never grow past it, only shrink.
    const wanted = Math.min(panel.scrollHeight, 256)
    const up = below < wanted && above > below

    panel.classList.add(...(up ? [ "bottom-full", "mb-1" ] : [ "top-full", "mt-1" ]))

    // NEVER TALLER THAN THE SIDE IT IS ON. Without this the panel keeps
    // its 16rem and hangs off the screen, which is the thin-strip problem
    // wearing a different hat — the list scrolls internally instead.
    const room = Math.max(up ? above : below, 96)
    panel.style.maxHeight = `${Math.min(wanted, room)}px`
  }

  // THE KEYBOARD ARRIVES AFTER THE LIST DOES. Focus opens the panel and
  // the keyboard animates in afterwards, so the measurement above is taken
  // against a viewport that is about to change. visualViewport reports the
  // new size when it settles; re-placing then is what makes the list end
  // up on the roomy side rather than behind the keyboard.
  watchViewport() {
    if (!window.visualViewport || this.onViewportChange) return

    this.onViewportChange = () => {
      if (!this.listTarget.hidden) this.placePanel()
    }
    window.visualViewport.addEventListener("resize", this.onViewportChange)
    window.visualViewport.addEventListener("scroll", this.onViewportChange)
  }

  // A blur landing on the list is a click on an option, not a dismissal.
  close(event) {
    if (event?.relatedTarget && this.listTarget.contains(event.relatedTarget)) return

    this.listTarget.hidden = true
    this.drop()
    this.activeIndex = -1
    // Whatever half-typed text is in the box is not a value. Put back what the
    // select actually holds.
    this.syncInputFromSelect()
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    const matches = query
      ? this.options.filter((option) => option.label.toLowerCase().includes(query))
      : this.options

    this.render(matches)
    this.listTarget.hidden = false
    this.placePanel()
    this.lift()
    this.activeIndex = matches.length ? 0 : -1
    this.highlight()
  }

  render(matches) {
    this.matches = matches

    if (matches.length === 0) {
      this.listTarget.innerHTML =
        `<div class="px-3 py-2.5 text-sm text-ink-faint">Nothing matches.</div>`
      return
    }

    // A heading whenever the group changes from the row before. Driven by
    // order rather than by nesting, so filtering — which drops rows and can
    // empty a group entirely — needs no special handling: a group with
    // nothing left in it simply never gets a heading drawn.
    let group = null

    this.listTarget.innerHTML = matches.map((option, index) => {
      let heading = ""

      if (option.group && option.group !== group) {
        group = option.group
        heading = `
          <div class="px-3 pb-1 pt-2.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-ink-faint">
            ${this.escape(group)}
          </div>`
      }

      // Indented under its heading, so an option reads as belonging to the
      // group above it rather than merely following it. Only when there IS a
      // heading — an ungrouped list keeps its original alignment.
      const indent = option.group ? "pl-6" : "px-3"

      return `${heading}
        <button type="button" role="option" data-index="${index}"
                data-action="mousedown->select-search#choose mouseenter->select-search#hover"
                class="combobox-option flex w-full items-center ${indent} pr-3 py-2 text-left text-sm text-ink">
          ${this.escape(option.label)}
        </button>`
    }).join("")
  }

  hover(event) {
    this.activeIndex = Number(event.currentTarget.dataset.index)
    this.highlight()
  }

  highlight() {
    this.listTarget.querySelectorAll(".combobox-option").forEach((element, index) => {
      element.classList.toggle("is-active", index === this.activeIndex)
    })
  }

  // mousedown, not click: the input's blur would close the list first.
  choose(event) {
    event.preventDefault()
    this.commit(Number(event.currentTarget.dataset.index))
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.listTarget.hidden) return this.open()
        this.activeIndex = Math.min(this.activeIndex + 1, (this.matches?.length || 0) - 1)
        this.highlight()
        this.scrollActiveIntoView()
        break
      case "ArrowUp":
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.highlight()
        this.scrollActiveIntoView()
        break
      case "Enter":
        if (this.listTarget.hidden || this.activeIndex < 0) return
        event.preventDefault()
        this.commit(this.activeIndex)
        break
      case "Escape":
        this.close()
        break
    }
  }

  commit(index) {
    const option = this.matches?.[index]
    if (!option) return

    this.selectTarget.value = option.value
    // dispatchEvent, not just assignment: anything listening for a change on
    // the real select hears nothing from a plain value set.
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))

    // Remembered so a later blur does not put the placeholder back over a
    // blank option somebody deliberately picked.
    this.chosen = true
    this.inputTarget.value = option.label
    this.listTarget.hidden = true
    this.drop()

    // AND THE FIELD LETS GO OF FOCUS.
    //
    // The list opens on focus, and a field that already HAS focus cannot
    // fire focus again — so after picking an option the control was dead
    // to the touch until you tapped elsewhere and came back. Cody, 11 Sep:
    // "after selecting an option, you have to blur and then refocus to
    // open the dropdown again; clicking on it whilst its already blurred
    // does nothing."
    //
    // Blurring is what makes the next tap a real focus again. toggle()
    // also reopens without needing focus, so this is belt and braces — it
    // matters wherever the element is still a text input. Touch only: on a
    // desktop it would steal focus from somebody tabbing through a form.
    if (this.touch) this.inputTarget.blur()
  }

  // NOTHING CHOSEN AND "ANY" CHOSEN ARE DIFFERENT STATES.
  //
  // Both have an empty value, so keying off the value alone put the
  // placeholder back the moment someone picked the blank option — which
  // reads as the click having failed, and invites picking it again.
  //
  // The distinction is whether a person has been near the field: an
  // untouched select shows its placeholder, a deliberately blanked one
  // shows the blank option's own label ("Any"). `chosen` is set by choose()
  // and survives the blur that calls this.
  syncInputFromSelect() {
    const selected = this.selectTarget.selectedOptions[0]
    const isBlank = !selected || selected.value === ""
    const text = !isBlank || this.chosen ? selected.text : ""

    this.setDisplay(text)
  }

  // EMPTY READS DIFFERENTLY ON THE TWO SHAPES. A text input with no value
  // shows its placeholder; a button shows nothing at all, because a
  // button's label IS its value — so the touch control would be a blank
  // box where the desktop one says "Choose one…".
  //
  // The placeholder is therefore written as the value there, and marked,
  // so the faint styling follows it and it is never mistaken for a choice.
  setDisplay(text) {
    if (this.inputTarget.type !== "button") {
      this.inputTarget.value = text
      return
    }

    const empty = text === ""
    this.inputTarget.value = empty ? this.inputTarget.placeholder : text
    this.inputTarget.classList.toggle("is-placeholder", empty)
  }

  scrollActiveIntoView() {
    this.listTarget.querySelectorAll(".combobox-option")[this.activeIndex]
      ?.scrollIntoView({ block: "nearest" })
  }

  // One shared implementation — see escape_html.js.
  escape(text) { return escapeHtml(text) }
}
